/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  FormControlLabel,
  Grid,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import type { Feature, FeatureCollection } from 'geojson';
import type { i18n, TFunction } from 'i18next';
import { isEqual } from 'lodash';
import type OlFeature from 'ol/Feature';
import React from 'react';
import type { FieldError, FieldErrors } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  getDeepProperty,
  ReactHookFormHiddenInput,
  ReactHookFormSelect,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  CustomIconButton,
  CustomTooltip,
  dateUtils,
} from '@opengeoweb/shared';
import type { MapPreset } from '@opengeoweb/store';
import {
  Options,
  Delete,
  DragHandle,
  Remove,
  Expire,
  Polygons,
  Cube,
  DrawPolygon,
  Copy,
  Info,
} from '@opengeoweb/theme';
import { CollapseToggleButton } from './CollapseToggleButton';
import {
  clipToApprovedArea,
  getApprovedAreaError,
  getOverlappingWarnings,
  hasAreaData,
  hasWarningStarted,
  isWarningFinalised,
} from './utils';
import type { ApprovedAreaError } from './utils';
import type { TimelineEditing } from './WarningEventTimeline';
import { useTimelineRange, WarningEventTimeline } from './WarningEventTimeline';
import type { DrawingListItem } from '../../../store/warningsDrawings/types';
import type { Area, WarningEventBody } from '../../../store/warningsForm/types';
import {
  PublicWarningStatus,
  WarningEventType,
} from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';
import { isPolygonFeature } from '../../DrawingToolForm/maskUtils';
import { warningLevelFeatureColors } from '../../PublicWarningsForm';
import { WarningLevel } from '../../PublicWarningsForm/LevelField';
import { WarningsMapView } from '../../WarningsMapView/WarningsMapView';

export enum Probability {
  TEN_TO_THIRTY = 10,
  THIRTY_TO_SIXTY = 30,
  SIXTY_TO_NINETY = 60,
  NINETY_TO_HUNDRED = 100,
}

export const probablityOptions = [
  { name: '10 - 30', value: Probability.TEN_TO_THIRTY },
  { name: '30 - 60', value: Probability.THIRTY_TO_SIXTY },
  { name: '60 - 90', value: Probability.SIXTY_TO_NINETY },
  { name: '90 - 100', value: Probability.NINETY_TO_HUNDRED },
];

const getWarningTitle = (
  status: PublicWarningStatus,
  areas: Area[],
  index: number,
  t: TFunction<'warn', i18n>,
): string => {
  const areaName =
    areas?.[0]?.objectName ||
    `${t('public-warning-event-type-warning')} ${index + 1}`;
  if (status === PublicWarningStatus.EXPIRED) {
    return `${areaName} - ${t('warning-event-warning-block-status-title-expired')}`;
  }
  if (status === PublicWarningStatus.WITHDRAWN) {
    return `${areaName} - ${t('warning-event-warning-block-status-title-withdrawn')}`;
  }
  return areaName;
};

const fieldsToValidate = ['validFrom', 'validUntil'];
const APPROVED_AREA_ERROR_TYPE = 'isWithinApprovedArea';
const APPROVED_AREA_ERROR_MESSAGES: Record<ApprovedAreaError, string> = {
  outside: 'warning-event-validation-area-outside-approved-area',
  error: 'warning-event-validation-area-approved-area-error',
};
const MAX_END_TIME_DAYS = 8; // max end time in days
const MIN_DURATION = 3600; // min duration of warning in seconds

const getValidationErrors = (
  fields: string[],
  errors: FieldErrors,
): { hasErrors: boolean; errors: FieldError[] } => {
  const fieldErrors = fields
    .map(
      (field) => getDeepProperty(field.split('.'), errors) as FieldError | null,
    )
    .filter((fieldError): fieldError is FieldError => !!fieldError);

  return { hasErrors: fieldErrors.length > 0, errors: fieldErrors };
};

type Validator = (...args: never[]) => boolean | string;

// Wraps every validator so it passes while isSkipped returns true
export const skipValidationWhen = <
  TValidators extends Record<string, Validator>,
>(
  isSkipped: () => boolean,
  validators: TValidators,
): TValidators =>
  Object.fromEntries(
    Object.entries(validators).map(([key, validate]) => [
      key,
      (...args: Parameters<typeof validate>): boolean | string =>
        isSkipped() ? true : validate(...args),
    ]),
  ) as TValidators;

// Outline only, no fill, as long as the feature has not been chosen
const buildOutlineOnlyGeoJSON = (
  geoJSON: FeatureCollection | undefined,
): FeatureCollection | undefined => {
  if (!geoJSON?.features?.length) {
    return undefined;
  }

  const blueFeatures = warningLevelFeatureColors(undefined, geoJSON);
  return {
    ...blueFeatures,
    features: blueFeatures.features.map((feature) => ({
      ...feature,
      properties: { ...feature.properties, 'fill-opacity': 0 },
    })),
  };
};

// Build the warning area for a single object of a drawing, colored by warning level.
// Only the part within the approved area may be assigned to the warning
export const buildAreaFromFeature = (
  feature: Feature,
  level?: WarningLevel,
  approvedArea?: FeatureCollection,
): Area => {
  const { geoJSON } = clipToApprovedArea(
    { type: 'FeatureCollection', features: [feature] },
    approvedArea,
  );
  return {
    objectName: (feature.properties?.objectName as string) ?? '',
    geoJSON: warningLevelFeatureColors(level, geoJSON),
  };
};

export const WarningEventItem: React.FC<{
  defaultMapPreset?: MapPreset;
  index: number;
  isReadOnly: boolean;
  isSnapshot?: boolean;
  isCompact: boolean;
  isDeleteButtonDisabled: boolean;
  selectedDrawing?: DrawingListItem;
  hoveredDrawing?: DrawingListItem;
  approvedArea?: FeatureCollection;
  isApprovedAreaLoading?: boolean;
  onExpireWithdraw: (status: PublicWarningStatus, index: number) => void;
  onToggleCompact: () => void;
  onAddDrawing: () => void;
  onDeleteWarning: (index: number) => void;
  onDuplicateWarning: (index: number) => void;
}> = ({
  defaultMapPreset,
  index,
  isReadOnly,
  isSnapshot = false,
  isCompact,
  isDeleteButtonDisabled = false,
  selectedDrawing,
  hoveredDrawing,
  approvedArea,
  isApprovedAreaLoading = false,
  onAddDrawing = (): void => {},
  onExpireWithdraw,
  onToggleCompact,
  onDeleteWarning,
  onDuplicateWarning,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [menuMapAnchorEl, setMenuMapAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const { t } = useWarningsTranslation();
  const { setValue, trigger, formState, getValues, getFieldState } =
    useFormContext();
  const { errors: fieldErrors } = formState;
  const timelineRange = useTimelineRange();
  const validFrom = useWatch({ name: `warnings.${index}.validFrom` });
  const validUntil = useWatch({ name: `warnings.${index}.validUntil` });
  const level = useWatch({ name: `warnings.${index}.level` });
  const warningStatus = useWatch({ name: `warnings.${index}.status` });
  const eventStatus = useWatch({ name: 'status' });
  const status = warningStatus || eventStatus;
  const linkedWarningId = useWatch({ name: `warnings.${index}.linked_to_id` });
  const warningAreas = useWatch({ name: `warnings.${index}.areas` });
  const { hasErrors: hasValidationErrors, errors: validationErrors } =
    getValidationErrors(
      fieldsToValidate.map((field) => `warnings.${index}.${field}`),
      fieldErrors,
    );
  const { hasErrors: hasProbabilityError, errors: probabilityError } =
    getValidationErrors([`warnings.${index}.probability`], fieldErrors);

  const { errors: areaError } = getValidationErrors(
    [`warnings.${index}.areas`],
    fieldErrors,
  );

  const { hasErrors: hasLevelError, errors: levelError } = getValidationErrors(
    [`warnings.${index}.level`],
    fieldErrors,
  );
  const revalidateOverlaps = React.useCallback((): void => {
    const warnings = getValues('warnings');
    warnings.forEach((_warning: WarningEventBody, warningIndex: number) => {
      void trigger(`warnings.${warningIndex}.validUntil`);
    });
  }, [getValues, trigger]);

  // Expired and withdrawn warnings, stored or only marked in the form, are not validated.
  // Read the status from the form, so this is up to date right after expiring/withdrawing
  const isFinalised = React.useCallback(
    (): boolean =>
      isWarningFinalised({
        status: getValues(`warnings.${index}.status`) || getValues('status'),
      }),
    [getValues, index],
  );

  const probabilityValidation = React.useCallback(
    (value: unknown): boolean | string => {
      const eventType = getValues('warningEventType');
      const warningLevel = getValues(`warnings.${index}.level`);
      // All is allowed for Warning
      if (eventType === WarningEventType.WARNING) {
        return true;
      }
      if (warningLevel === WarningLevel.moderate) {
        // For yellow early warning: all phenomena, only allow 30-60, 60-90 and 90-100
        return value === Probability.TEN_TO_THIRTY
          ? t('warning-event-validation-no-probability-yellow-other')
          : true;
      }
      return true;
    },
    [getValues, index, t],
  );

  const editing: TimelineEditing = React.useMemo(
    () => ({
      onTimesChange: (newValidFrom: string, newValidUntil: string): void => {
        setValue(`warnings.${index}.validFrom`, newValidFrom, {
          shouldDirty: true,
        });
        setValue(`warnings.${index}.validUntil`, newValidUntil, {
          shouldDirty: true,
        });
        void trigger(`warnings.${index}.validFrom`);
        revalidateOverlaps();
      },
      onLevelChange: (newLevel: WarningLevel): void => {
        setValue(`warnings.${index}.level`, newLevel, { shouldDirty: true });

        const geoJSON = warningLevelFeatureColors(
          newLevel,
          warningAreas?.[0]?.geoJSON,
        );
        setValue(`warnings.${index}.areas.0.geoJSON`, geoJSON, {
          shouldDirty: true,
        });
        void trigger(`warnings.${index}.level`);
        void trigger(`warnings.${index}.probability`);
        revalidateOverlaps();
      },
    }),
    [index, setValue, warningAreas, trigger, revalidateOverlaps],
  );
  const readOnlyWarning =
    isReadOnly ||
    warningStatus === PublicWarningStatus.EXPIRED ||
    warningStatus === PublicWarningStatus.WITHDRAWN ||
    eventStatus === PublicWarningStatus.EXPIRED ||
    eventStatus === PublicWarningStatus.WITHDRAWN;

  const assignedFeatureId = warningAreas?.[0]?.geoJSON?.features?.[0]?.id;

  const features = React.useMemo(
    () => selectedDrawing?.geoJSON?.features ?? [],
    [selectedDrawing],
  );

  // Assign the object picked from the selected drawing as the area of this warning
  const onAssignFeature = React.useCallback(
    (featureId: string): void => {
      const feature = features.find((item) => String(item.id) === featureId);
      // the approved area has to be known before an object can be checked against it
      if (
        !feature ||
        featureId === assignedFeatureId ||
        isApprovedAreaLoading
      ) {
        return;
      }
      const area = buildAreaFromFeature(
        feature,
        level as WarningLevel,
        approvedArea,
      );
      setValue(`warnings.${index}.areas`, [area], { shouldDirty: true });
      void trigger(`warnings.${index}.areas`);
    },
    [
      features,
      assignedFeatureId,
      isApprovedAreaLoading,
      approvedArea,
      level,
      index,
      setValue,
      trigger,
    ],
  );

  const hasAssignedArea = !!warningAreas?.[0]?.geoJSON?.features?.length;

  const isFinalisedWarning = isWarningFinalised({ status });
  const approvedAreaError = React.useMemo(
    () =>
      isFinalisedWarning
        ? undefined
        : getApprovedAreaError(warningAreas, approvedArea),
    [isFinalisedWarning, warningAreas, approvedArea],
  );

  const showApprovedAreaError =
    !!approvedAreaError &&
    (!readOnlyWarning ||
      areaError.some(
        (validationError) => validationError.type === APPROVED_AREA_ERROR_TYPE,
      ));
  const areaErrorMessages = [
    ...areaError
      .filter(
        (validationError) => validationError.type !== APPROVED_AREA_ERROR_TYPE,
      )
      .map((validationError) => validationError.message),
    ...(showApprovedAreaError
      ? [t(APPROVED_AREA_ERROR_MESSAGES[approvedAreaError])]
      : []),
  ];
  const hasAreaErrorMessages = areaErrorMessages.length > 0;
  // An assigned object is only changed when it has been clipped to the approved area.
  // After saving the area is not dirty anymore, then the banner is removed
  const isAreaDirty = getFieldState(
    `warnings.${index}.areas`,
    formState,
  ).isDirty;
  const assignedGeometry = warningAreas?.[0]?.geoJSON?.features?.[0]?.geometry;
  const originalGeometry = features.find(
    (feature) =>
      assignedFeatureId !== undefined && feature.id === assignedFeatureId,
  )?.geometry;
  const showClippedWarning =
    isAreaDirty &&
    !!assignedGeometry &&
    !!originalGeometry &&
    !isEqual(assignedGeometry, originalGeometry);

  React.useEffect(() => {
    if (
      readOnlyWarning ||
      isApprovedAreaLoading ||
      hasAssignedArea ||
      features.length !== 1
    ) {
      return;
    }
    onAssignFeature(String(features[0].id));
  }, [
    readOnlyWarning,
    isApprovedAreaLoading,
    hasAssignedArea,
    features,
    onAssignFeature,
  ]);

  // As long as no area has been assigned to this warning, show all features of the
  // selected drawing in blue. Once area is chosen, only show that one, in level color
  const displayedGeoJSON = React.useMemo(() => {
    if (hasAssignedArea) {
      return warningAreas[0].geoJSON as FeatureCollection;
    }
    return buildOutlineOnlyGeoJSON(selectedDrawing?.geoJSON);
  }, [hasAssignedArea, warningAreas, selectedDrawing]);

  const previewGeoJSON = React.useMemo(() => {
    if (!hoveredDrawing || hoveredDrawing.id === selectedDrawing?.id) {
      return undefined;
    }
    return buildOutlineOnlyGeoJSON(hoveredDrawing.geoJSON);
  }, [hoveredDrawing, selectedDrawing?.id]);

  // Picking a feature directly on the map, only possible on an expanded, editable map
  const canPickFeatureOnMap =
    !isCompact &&
    !readOnlyWarning &&
    !isApprovedAreaLoading &&
    !hasAssignedArea &&
    features.length > 0;

  const clickOnFeature = React.useMemo(
    () =>
      canPickFeatureOnMap
        ? {
            onClick: (clickedFeatures: OlFeature[]): void => {
              const featureId = clickedFeatures[0]?.getId();
              if (featureId === undefined) {
                return;
              }
              onAssignFeature(String(featureId));
            },
            hitTolerance: 5,
          }
        : undefined,
    [canPickFeatureOnMap, onAssignFeature],
  );

  const hoverSelect = React.useMemo(
    () => (canPickFeatureOnMap ? { active: true } : undefined),
    [canPickFeatureOnMap],
  );

  // Until we implement the full validation, set every field to be required when publishing
  const { isRequired, isDraft } = useDraftFormHelpers();

  const probabilityRules = {
    validate: skipValidationWhen(isFinalised, {
      isRequired,
      probabilityValidation,
    }),
  };

  const collapseToggleButton = (
    <CollapseToggleButton
      collapseLabel={t('public-warning-event-collapse')}
      expandLabel={t('public-warning-event-expand')}
      isCollapsed={isCompact}
      onToggle={onToggleCompact}
    />
  );

  const originalWarning = linkedWarningId
    ? (
        formState.defaultValues?.warnings as
          | Partial<WarningEventBody>[]
          | undefined
      )?.find((warning) => warning?.linked_to_id === linkedWarningId)
    : undefined;
  // Without an original warning it has never been published, so it has not started
  const isActive =
    !!originalWarning?.validFrom &&
    hasWarningStarted({ validFrom: originalWarning.validFrom });

  const onExpire = (): void => {
    onExpireWithdraw(PublicWarningStatus.EXPIRED, index);
  };

  const onWithdraw = (): void => {
    onExpireWithdraw(PublicWarningStatus.WITHDRAWN, index);
  };

  const warningTitle = getWarningTitle(status, warningAreas, index, t);

  const mapTitle =
    selectedDrawing?.objectName || warningAreas?.[0]?.objectName || undefined;
  return (
    <>
      {isCompact ? (
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
            marginBottom: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: 56,
          }}
        >
          <CustomIconButton
            className="handle"
            disabled={readOnlyWarning}
            sx={{ cursor: 'grab', flexShrink: 0 }}
            variant="flat"
          >
            <DragHandle />
          </CustomIconButton>
          <CustomTooltip title={warningTitle}>
            <Typography
              component="h3"
              sx={{
                fontSize: 14,
                flexShrink: 0,
                width: `${hasValidationErrors || hasProbabilityError || hasAreaErrorMessages || hasLevelError ? '93px' : '125px'}`,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {warningTitle}
            </Typography>
          </CustomTooltip>
          {!!(
            hasValidationErrors ||
            hasProbabilityError ||
            hasAreaErrorMessages ||
            hasLevelError
          ) && (
            <CustomTooltip
              title={`${t('warning-event-validation-warning-incomplete')}: ${validationErrors.map(
                (validationError) => `${validationError.message}.`,
              )} ${probabilityError.map((validationError) =>
                validationError.type === 'isRequired'
                  ? ` ${t('warning-event-validation-propbability-collapsed')}.`
                  : ` ${validationError.message}.`,
              )}${areaErrorMessages.map((message) => ` ${message}.`)}
              ${levelError.map(
                (validationError) => ` ${validationError.message}.`,
              )}`}
            >
              <span>
                <Info
                  sx={{
                    marginTop: 0.5,
                    color: `geowebColors.functional.error`,
                  }}
                />
              </span>
            </CustomTooltip>
          )}
          <Box
            sx={{
              width: 100,
              height: 56,
            }}
          >
            <WarningsMapView
              defaultMapPreset={defaultMapPreset}
              geojsonFeature={displayedGeoJSON}
              isSnapshot={isSnapshot}
              previewGeojsonFeature={previewGeoJSON}
              shouldFitPreviewIntoView
              showMapMousePosition={false}
              showZoomControls={false}
              size={{ height: '56px', width: '100%' }}
            />
          </Box>
          <Box sx={{ flex: 1, height: 56, minWidth: 0 }}>
            <WarningEventTimeline
              end={timelineRange.end}
              isCompact
              level={level ?? ''}
              start={timelineRange.start}
              status={status}
              validFrom={validFrom ?? ''}
              validUntil={validUntil ?? ''}
            />
          </Box>
          {collapseToggleButton}
          <ReactHookFormHiddenInput
            name={`warnings.${index}.probability`}
            rules={probabilityRules}
          />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
            marginBottom: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 210px 1fr',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <CustomIconButton
                className="handle"
                disabled={readOnlyWarning}
                sx={{ cursor: 'grab', flexShrink: 0 }}
                variant="flat"
              >
                <DragHandle />
              </CustomIconButton>
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: 260 }}>
                <Typography component="h3" sx={{ fontSize: 14 }}>
                  {warningTitle}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '& .MuiFormControl-root': { width: '100%' },
              }}
            >
              <ReactHookFormSelect
                disabled={readOnlyWarning}
                label={t('warning-event-probability')}
                name={`warnings.${index}.probability`}
                rules={probabilityRules}
              >
                {probablityOptions.map((probability) => (
                  <MenuItem key={probability.value} value={probability.value}>
                    {probability.name} %
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                justifySelf: 'end',
              }}
            >
              <CustomIconButton
                disabled={readOnlyWarning}
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                variant="tool"
              >
                <Options />
              </CustomIconButton>
              {collapseToggleButton}
              <Menu
                anchorEl={menuAnchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                onClose={() => setMenuAnchorEl(null)}
                open={Boolean(menuAnchorEl)}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <ListSubheader sx={{ fontSize: 12, lineHeight: '1.5' }}>
                  {t('area-manager-share-menu-options')}
                </ListSubheader>
                <MenuItem
                  onClick={() => {
                    setMenuAnchorEl(null);
                    onDuplicateWarning(index);
                  }}
                >
                  <ListItemText>
                    {t('public-warning-event-duplicate')}
                  </ListItemText>
                  <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                    <Copy />
                  </ListItemIcon>
                </MenuItem>
                {
                  // Only show delete item for unsaved, draft & draft published warnings that are new (aka have not already been published)
                  (eventStatus === undefined ||
                    eventStatus === PublicWarningStatus.DRAFT ||
                    eventStatus === PublicWarningStatus.DRAFT_PUBLISHED) &&
                    linkedWarningId === undefined && (
                      <MenuItem
                        disabled={isDeleteButtonDisabled}
                        onClick={() => {
                          setMenuAnchorEl(null);
                          onDeleteWarning(index);
                        }}
                      >
                        <ListItemText>
                          {t('public-warning-event-delete')}
                        </ListItemText>
                        <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                          <Delete />
                        </ListItemIcon>
                      </MenuItem>
                    )
                }
                {
                  // Event should already have been published but not already withdrawn or expired
                  linkedWarningId !== undefined &&
                    eventStatus === PublicWarningStatus.DRAFT_PUBLISHED &&
                    warningStatus !== PublicWarningStatus.EXPIRED &&
                    warningStatus !== PublicWarningStatus.WITHDRAWN && (
                      <>
                        <MenuItem
                          disabled={!isActive}
                          onClick={() => {
                            setMenuAnchorEl(null);
                            onExpire();
                          }}
                        >
                          <ListItemText>
                            {t('warning-list-button-expire')}
                          </ListItemText>
                          <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                            <Expire />
                          </ListItemIcon>
                        </MenuItem>
                        <MenuItem
                          disabled={isActive}
                          onClick={() => {
                            setMenuAnchorEl(null);
                            onWithdraw();
                          }}
                        >
                          <ListItemText>
                            {t('warning-list-button-withdraw')}
                          </ListItemText>
                          <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                            <Remove />
                          </ListItemIcon>
                        </MenuItem>
                      </>
                    )
                }
              </Menu>
            </Box>
          </Box>
          {(!!hasValidationErrors || !!hasLevelError) && (
            <Box sx={{ marginBottom: 2 }}>
              <AlertBanner
                info={
                  <>
                    {validationErrors.map((validationError, errorIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={errorIndex}>{validationError.message}</div>
                    ))}
                    {levelError.map((error, errorIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={errorIndex}>{error.message}</div>
                    ))}
                  </>
                }
                severity="error"
                title={t('warning-event-validation-warning-incomplete')}
              />
            </Box>
          )}
          <Grid container spacing={1}>
            <Grid size={4}>
              <Box
                sx={{
                  height: 200,

                  position: 'relative',
                }}
              >
                {!!(hasAreaErrorMessages || showClippedWarning) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '0px',
                      left: '40px',
                      width: 'calc(100% - 40px)',
                      zIndex: 1,
                    }}
                  >
                    {hasAreaErrorMessages ? (
                      <AlertBanner
                        info={
                          <>
                            {areaErrorMessages.map((message, errorIndex) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div key={errorIndex}>{message}</div>
                            ))}
                          </>
                        }
                        severity="error"
                        title={t('warning-event-validation-warning-incomplete')}
                      />
                    ) : (
                      <AlertBanner
                        dataTestId="warning-event-area-clipped-banner"
                        info={t('warning-event-area-clipped-info')}
                        // show a closed banner again when another object is clipped
                        key={String(assignedFeatureId)}
                        severity="warning"
                        shouldClose
                        title={t('warning-event-area-clipped-title')}
                      />
                    )}
                  </Box>
                )}
                <WarningsMapView
                  clickOnFeature={clickOnFeature}
                  defaultMapPreset={defaultMapPreset}
                  geojsonFeature={displayedGeoJSON}
                  hoverSelect={hoverSelect}
                  isSnapshot={isSnapshot}
                  previewGeojsonFeature={previewGeoJSON}
                  shouldFitPreviewIntoView
                  showMapMousePosition={false}
                  size={{ height: '100%', width: '100%' }}
                />
                {!readOnlyWarning && (
                  <>
                    {
                      // Only show drawing loader on first warning
                      index === 0 && (
                        <CustomIconButton
                          isSelected={false}
                          onClick={onAddDrawing}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            bottom: 40,
                            zIndex: 1,
                          }}
                          tooltipTitle={t('warning-event-button-add-drawing')}
                          variant="tool"
                        >
                          <Polygons />
                        </CustomIconButton>
                      )
                    }
                    <CustomIconButton
                      disabled={isApprovedAreaLoading}
                      isSelected={false}
                      onClick={(e) => setMenuMapAnchorEl(e.currentTarget)}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        bottom: 8,
                        zIndex: 1,
                      }}
                      tooltipTitle={t('warning-event-button-assign-object')}
                      variant="tool"
                    >
                      <Cube />
                    </CustomIconButton>
                    <Popover
                      anchorEl={menuMapAnchorEl}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      onClose={() => setMenuMapAnchorEl(null)}
                      open={Boolean(menuMapAnchorEl)}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      <Box sx={{ padding: 1 }}>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 500, padding: 1 }}
                        >
                          {t('warning-event-objects-menu-title')}
                        </Typography>
                        {features.length > 0 ? (
                          <RadioGroup
                            name={`warnings.${index}.areas`}
                            onChange={(_event, value): void => {
                              onAssignFeature(value);
                              setMenuMapAnchorEl(null);
                            }}
                            sx={{ paddingX: 1 }}
                            value={assignedFeatureId ?? ''}
                          >
                            {features.map((feature, featureIndex) => (
                              <FormControlLabel
                                control={<Radio size="small" />}
                                key={String(feature.id)}
                                label={
                                  feature?.properties?.objectName ||
                                  t('area-drawing-manager-object-name', {
                                    number: featureIndex + 1,
                                  })
                                }
                                slotProps={{
                                  typography: { sx: { fontSize: 14 } },
                                }}
                                value={String(feature.id)}
                              />
                            ))}
                          </RadioGroup>
                        ) : (
                          <Typography sx={{ padding: 1 }}>
                            {t('warning-event-objects-menu-no-objects')}
                          </Typography>
                        )}
                      </Box>
                    </Popover>
                  </>
                )}
              </Box>
              {!!mapTitle && (
                <Box
                  sx={{
                    border: '1px solid',
                    mt: 1,
                    borderColor: 'geowebColors.cards.cardContainerBorder',
                    height: '32px',
                  }}
                >
                  <Grid container sx={{ height: '100%' }}>
                    <Grid
                      size={9}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        pl: '8px',
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                        {mapTitle}
                      </Typography>
                    </Grid>
                    <Grid
                      size={3}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CustomIconButton
                        disabled
                        isSelected={false}
                        onClick={() => {}}
                        sx={{ mr: '8px' }}
                        tooltipTitle={t(
                          'warning-event-map-edit-drawing-tooltip',
                        )}
                        variant="flat"
                      >
                        <DrawPolygon />
                      </CustomIconButton>
                      <CustomIconButton
                        disabled
                        isSelected={false}
                        onClick={() => {}}
                        sx={{ mr: '8px' }}
                        tooltipTitle={t(
                          'warning-event-map-remove-drawing-tooltip',
                        )}
                        variant="flat"
                      >
                        <Delete />
                      </CustomIconButton>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid size={8}>
              <Box sx={{ height: '100%' }}>
                <WarningEventTimeline
                  editing={readOnlyWarning ? undefined : editing}
                  end={timelineRange.end}
                  isActive={isActive}
                  level={level ?? ''}
                  start={timelineRange.start}
                  status={status}
                  validFrom={validFrom ?? ''}
                  validUntil={validUntil ?? ''}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      <ReactHookFormHiddenInput
        name={`warnings.${index}.areas`}
        rules={{
          validate: skipValidationWhen(isFinalised, {
            isRequired: (value: Area[]): boolean | string =>
              isDraft() || hasAreaData(value)
                ? true
                : t('warning-event-validation-area-missing'),
            isPolygonalFeature: (value: Area[]): boolean | string =>
              !hasAreaData(value) ||
              isPolygonFeature(value?.[0]?.geoJSON?.features?.[0])
                ? true
                : t('warning-event-validation-area-non-polygon'),
            // only blocks publishing
            [APPROVED_AREA_ERROR_TYPE]: (value: Area[]): boolean | string => {
              if (isDraft()) {
                return true;
              }
              const error = getApprovedAreaError(value, approvedArea);
              return error ? t(APPROVED_AREA_ERROR_MESSAGES[error]) : true;
            },
          }),
        }}
      />
      <ReactHookFormHiddenInput
        name={`warnings.${index}.validFrom`}
        rules={{
          validate: skipValidationWhen(isFinalised, {
            minStartTime: (value: string): boolean | string => {
              const now = dateUtils.getNowUtc();
              // If new warning added to published event -> no start time before current time
              const newWarning =
                status === PublicWarningStatus.DRAFT_PUBLISHED &&
                !linkedWarningId;
              // If existing, non-active warning on published event -> no start time before current time
              const nonActivePublishedWarning =
                (status === PublicWarningStatus.DRAFT_PUBLISHED ||
                  status === PublicWarningStatus.PUBLISHED) &&
                !!linkedWarningId &&
                !isActive;

              return (status === undefined ||
                status === PublicWarningStatus.DRAFT ||
                newWarning ||
                nonActivePublishedWarning) &&
                dateUtils.unixFromISOString(value) < dateUtils.unix(now)
                ? t('warning-event-validation-min-start-time-draft')
                : true;
            },
          }),
        }}
      />
      <ReactHookFormHiddenInput
        name={`warnings.${index}.validUntil`}
        rules={{
          validate: skipValidationWhen(isFinalised, {
            maxEndTime: (value: string): boolean | string => {
              const dayZero = dateUtils.startOfDayUTC(dateUtils.getNowUtc());
              const maxEnd = dateUtils.add(dayZero, {
                days: MAX_END_TIME_DAYS,
              });
              return dateUtils.unixFromISOString(value) > dateUtils.unix(maxEnd)
                ? t('warning-event-validation-max-end-time', {
                    MAX_DAYS: MAX_END_TIME_DAYS,
                  })
                : true;
            },
            minDuration: (value: string): boolean | string => {
              if (isDraft()) {
                return true;
              }
              const validFrom = getValues(`warnings.${index}.validFrom`);
              const timeDiff =
                dateUtils.unixFromISOString(value) -
                dateUtils.unixFromISOString(validFrom);
              return timeDiff < MIN_DURATION
                ? t('warning-event-validation-min-duration')
                : true;
            },
            noOverlap: (
              _value: string,
              formValues: unknown,
            ): boolean | string => {
              if (isDraft()) {
                return true;
              }
              const warnings =
                (formValues as { warnings?: WarningEventBody[] }).warnings ??
                [];
              return getOverlappingWarnings(warnings, index).length > 0
                ? t('warning-event-validation-overlap')
                : true;
            },
            notFinished: (value: string): boolean | string => {
              if (isDraft()) {
                return true;
              }
              // Only published warnings can be expired, the backend no longer expires them automatically
              if (!getValues(`warnings.${index}.linked_to_id`)) {
                return true;
              }
              return dateUtils.unixFromISOString(value) <=
                dateUtils.unix(dateUtils.getNowUtc())
                ? t('warning-event-validation-warning-finished')
                : true;
            },
          }),
        }}
      />
    </>
  );
};

export default WarningEventItem;
